import { useEffect, useState } from "react";
import { LeadAPI } from "api/lead/service";
import {
  User,
  Phone,
  Mail,
  MapPin,
  Building2,
  Ruler,
  IndianRupee,
  Calendar,
  Clock,
  Edit,
  FileText,
  FileSpreadsheet,
  Activity,
  StickyNote,
  Video,
  Plus,
  Send,
  MoreVertical,
  Pencil,
  Trash2,
  ExternalLink,
  MessageCircle,
  X,
} from "lucide-react";

function formatActivityTimestamp(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime()))
      return String(iso).replace("T", " ").slice(0, 16);
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    const hh = String(d.getHours()).padStart(2, "0");
    const mi = String(d.getMinutes()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd} ${hh}:${mi}`;
  } catch (_) {
    return String(iso).replace("T", " ").slice(0, 16);
  }
}

function normalizeActivity(a) {
  if (!a) return null;
  return {
    id: String(a.id ?? a.pk ?? ""),
    description: a.description || "",
    lead: a.lead,
    date: formatActivityTimestamp(
      a.created_on || a.createdAt || a.created_at || a.updated_on || "",
    ),
    by: a.created_by || a.updated_by || "System",
  };
}

function normalizeNote(n) {
  if (!n) return null;
  return {
    id: String(n.id ?? n.pk ?? ""),
    text: n.text || "",
    lead: n.lead,
    timestamp: formatActivityTimestamp(
      n.created_on || n.createdAt || n.created_at || n.updated_on || "",
    ),
    author: n.created_by || n.updated_by || "You",
  };
}

function normalizeMeeting(m) {
  if (!m) return null;
  const timeRaw = String(m.time || "").trim();
  const timeShort = timeRaw ? timeRaw.slice(0, 5) : "";
  return {
    id: String(m.id ?? m.pk ?? ""),
    lead: m.lead,
    title: m.title || "",
    description: m.description || "",
    date: m.date || "",
    time: timeShort || timeRaw,
    link: m.link || "",
    createdOn: m.created_on || m.createdAt || m.created_at || "",
  };
}

function meetingStartMs(meeting) {
  if (!meeting?.date) return NaN;
  const time = meeting?.time ? String(meeting.time) : "00:00";
  // local time is OK for UI split
  const d = new Date(
    `${meeting.date}T${time.length === 5 ? `${time}:00` : time}`,
  );
  return d.getTime();
}

function toApiTime(t) {
  const s = String(t || "").trim();
  if (!s) return "";
  // input type="time" returns "HH:MM"
  if (/^\d{2}:\d{2}$/.test(s)) return `${s}:00`;
  if (/^\d{2}:\d{2}:\d{2}$/.test(s)) return s;
  return s;
}

export function LeadView({
  client,
  onCreateScope,
  onCreateQuotation,
  onDelete,
}) {
  const [activeSubTab, setActiveSubTab] = useState("overview");
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState("");
  const [isLoadingNotes, setIsLoadingNotes] = useState(false);
  const [notesError, setNotesError] = useState("");
  const [isCreatingNote, setIsCreatingNote] = useState(false);
  const [activities, setActivities] = useState([]);
  const [isLoadingActivities, setIsLoadingActivities] = useState(false);
  const [activitiesError, setActivitiesError] = useState("");
  const [newActivity, setNewActivity] = useState("");
  const [isCreatingActivity, setIsCreatingActivity] = useState(false);
  const [isAddMeetingOpen, setIsAddMeetingOpen] = useState(false);
  const [meetings, setMeetings] = useState([]);
  const [isLoadingMeetings, setIsLoadingMeetings] = useState(false);
  const [meetingsError, setMeetingsError] = useState("");
  const [isCreatingMeeting, setIsCreatingMeeting] = useState(false);
  const [newMeeting, setNewMeeting] = useState({
    title: "",
    date: "",
    time: "",
    description: "",
    link: "",
  });
  const [isCreateScopeOpen, setIsCreateScopeOpen] = useState(false);
  const [isCreatingScope, setIsCreatingScope] = useState(false);
  const [scopeError, setScopeError] = useState("");
  const [newScope, setNewScope] = useState({
    name: "",
    tentative_start: "",
    property_size: "",
    budget: "",
    status: "draft",
  });
  const [isCreateQuotationOpen, setIsCreateQuotationOpen] = useState(false);
  const [isCreatingQuotation, setIsCreatingQuotation] = useState(false);
  const [quotationError, setQuotationError] = useState("");
  const [newQuotation, setNewQuotation] = useState({
    name: "",
    payment_terms: "",
    terms_conditions: "",
  });

  const [leadDetails, setLeadDetails] = useState(client);
  const [leadRefreshKey, setLeadRefreshKey] = useState(0);
  const [isEditInfoOpen, setIsEditInfoOpen] = useState(false);
  const [editInfoForm, setEditInfoForm] = useState({
    name: "",
    phone: "",
    email: "",
    city: "",
  });
  const [isSavingInfo, setIsSavingInfo] = useState(false);
  const [editInfoError, setEditInfoError] = useState("");

  const [phoneError, setPhoneError] = useState("");

  const [isEditProjectOpen, setIsEditProjectOpen] = useState(false);
  const [editProjectForm, setEditProjectForm] = useState({
    project_type: "",
    property_size: "",
    budget: "",
    start_date: "",
  });
  const [isSavingProject, setIsSavingProject] = useState(false);
  const [editProjectError, setEditProjectError] = useState("");

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState("");

  const leadInfo = leadDetails || client;
  const leadName = String(leadInfo?.name || "");
  const leadPhone = String(leadInfo?.phone || "");
  const leadEmail = String(leadInfo?.email || "");
  const leadCity = String(leadInfo?.city || "");
  const leadProjectType =
    leadInfo?.projectType || leadInfo?.project_type || "Interior";
  const leadPropertySize =
    leadInfo?.propertySize || leadInfo?.property_size || "1,200 sq.ft";
  const leadStartDate = leadInfo?.startDate || leadInfo?.start_date || "";
  const leadCreatedAt =
    leadInfo?.createdAt ||
    String(
      leadInfo?.created_on || leadInfo?.created_at || leadInfo?.createdAt || "",
    ).split("T")[0] ||
    "";
  const leadNotes = leadInfo?.notes || leadInfo?.remarks || "";
  const leadBudgetNumber = (() => {
    const b = leadInfo?.budget;
    if (typeof b === "number") return Number.isFinite(b) ? b : null;
    const n = Number(String(b ?? "").replace(/,/g, ""));
    return Number.isFinite(n) ? n : null;
  })();

  const leadStatusRaw = String(leadInfo?.status || "").toLowerCase();
  const scopeStatus = String(
    leadInfo?.scope?.status ||
      leadInfo?.scope_status ||
      leadInfo?.scopeStatus ||
      "",
  ).toLowerCase();
  const isScopeCompleted = scopeStatus === "completed";
  const isLeadCompleted = leadStatusRaw === "completed";
  const isLeadConverted = ["completed", "converted"].includes(leadStatusRaw);
  const canDeleteLead = !isLeadConverted;
  const isEditInfoDisabled = isScopeCompleted || isLeadCompleted;
  const canCreateScope = !["completed", "active", "quotation_active"].includes(
    String(leadInfo?.status || "").toLowerCase(),
  );
  const resolvedLeadId = client?.id || leadInfo?.id;

  const [openMenu, setOpenMenu] = useState(null); // { type: "activity"|"note"|"meeting", id: string }
  const [editModal, setEditModal] = useState(null); // { type, item }
  const [editValue, setEditValue] = useState(""); // activity.description OR note.text
  const [editMeeting, setEditMeeting] = useState({
    title: "",
    date: "",
    time: "",
    description: "",
    link: "",
  });
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const [editError, setEditError] = useState("");

  const closeMenu = () => setOpenMenu(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openMenu && !event.target.closest(".menu-container")) {
        closeMenu();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [openMenu]);

  const subTabs = [
    { id: "overview", label: "Overview", icon: User },
    { id: "activity", label: "Activity", icon: Activity },
    { id: "notes", label: "Notes", icon: StickyNote },
    { id: "meetings", label: "Meetings", icon: Video },
  ];

  const getStatusBadge = (status) => {
    const styles = {
      lead: "bg-orange-100 text-orange-700",
      active: "bg-blue-100 text-blue-700",
      completed: "bg-green-100 text-green-700",
    };
    const labels = {
      lead: "Lead",
      active: "Active",
      completed: "Completed",
    };
    return (
      <span
        className={`px-2 py-0.5 rounded text-xs font-medium ${styles[status]}`}
      >
        {labels[status]}
      </span>
    );
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const handleOpenEditInfo = () => {
    if (isEditInfoDisabled) return;
    setEditInfoError("");
    setPhoneError(""); // ADD THIS
    setEditInfoForm({
      name: leadInfo?.name || "",
      phone: leadInfo?.phone?.startsWith("+91") // Strip +91 if stored with prefix
        ? leadInfo.phone.slice(3)
        : leadInfo?.phone || "",
      email: leadInfo?.email || "",
      city: leadInfo?.city || "",
    });
    setIsEditInfoOpen(true);
  };
  const handlePhoneInput = (e) => {
    const onlyNums = e.target.value.replace(/[^0-9]/g, "");
    e.target.value = onlyNums;
    setEditInfoForm((prev) => ({ ...prev, phone: onlyNums }));
    if (onlyNums.length > 0 && onlyNums.length < 10) {
      setPhoneError("Contact number must be exactly 10 digits");
    } else {
      setPhoneError("");
    }
  };
  const handleSaveEditInfo = async () => {
    const orgId = LeadAPI.orgId();
    const leadId = resolvedLeadId;
    if (!orgId || !leadId) {
      setEditInfoError("Organization/Lead not found");
      return;
    }

    const payload = {
      name: String(editInfoForm.name || "").trim(),
      phone: "+91" + String(editInfoForm.phone || "").trim(), // re-attach prefix
      email: String(editInfoForm.email || "").trim(),
      city: String(editInfoForm.city || "").trim(),
    };

    if (!payload.name || !editInfoForm.phone) {
      setEditInfoError("Name and phone are required");
      return;
    }

    // ADD THIS BLOCK
    if (editInfoForm.phone.length !== 10) {
      setPhoneError("Contact number must be exactly 10 digits");
      return;
    }

    setIsSavingInfo(true);
    setEditInfoError("");
    try {
      const res = await LeadAPI.updateLead(orgId, leadId, payload);
      const updated = res?.data || res;
      if (updated) {
        setLeadDetails((prev) => ({ ...(prev || {}), ...updated }));
      }
      setIsEditInfoOpen(false);
      setLeadRefreshKey((prev) => prev + 1);
    } catch (e) {
      setEditInfoError(e?.message || "Failed to update client details");
    } finally {
      setIsSavingInfo(false);
    }
  };
  const handleOpenEditProject = () => {
    if (isEditInfoDisabled) return;
    setEditProjectError("");
    setEditProjectForm({
      project_type:
        leadInfo?.project_type || leadInfo?.projectType || "interior",
      property_size: leadInfo?.property_size || leadInfo?.propertySize || "",
      budget: leadBudgetNumber != null ? String(leadBudgetNumber) : "",
      start_date: leadInfo?.start_date || leadInfo?.startDate || "",
    });
    setIsEditProjectOpen(true);
  };

  const handleSaveEditProject = async () => {
    const orgId = LeadAPI.orgId();
    const leadId = resolvedLeadId;
    if (!orgId || !leadId) {
      setEditProjectError("Organization/Lead not found");
      return;
    }

    const budgetNum = Number(
      String(editProjectForm.budget || "").replace(/,/g, ""),
    );

    const payload = {
      project_type: editProjectForm.project_type,
      property_size: String(editProjectForm.property_size || "").trim(),
      budget: Number.isFinite(budgetNum) ? budgetNum : null,
      start_date: editProjectForm.start_date || null,
    };

    setIsSavingProject(true);
    setEditProjectError("");
    try {
      const res = await LeadAPI.updateLead(orgId, leadId, payload);
      const updated = res?.data || res;
      if (updated) {
        setLeadDetails((prev) => ({ ...(prev || {}), ...updated }));
      }
      setIsEditProjectOpen(false);
      setLeadRefreshKey((prev) => prev + 1);
    } catch (e) {
      setEditProjectError(e?.message || "Failed to update project details");
    } finally {
      setIsSavingProject(false);
    }
  };

  const handleConfirmDeleteLead = async () => {
    const orgId = LeadAPI.orgId();
    const leadId = resolvedLeadId;
    if (!canDeleteLead) {
      setDeleteError("Converted leads cannot be deleted");
      return;
    }

    if (!orgId || !leadId) {
      setDeleteError("Organization/Lead not found");
      return;
    }

    setIsDeleting(true);
    setDeleteError("");
    try {
      await LeadAPI.deleteLead(orgId, leadId);
      setIsDeleteModalOpen(false);
      if (onDelete) onDelete();
    } catch (e) {
      setDeleteError(e?.message || "Failed to delete lead");
    } finally {
      setIsDeleting(false);
    }
  };

  const isEditInfoSaveDisabled =
    isSavingInfo ||
    !String(editInfoForm.name || "").trim() ||
    !String(editInfoForm.phone || "").trim() ||
    String(editInfoForm.phone || "").trim().length !== 10;

  const handleAddNote = async () => {
    const text = newNote.trim();
    if (!text) return;

    const orgId = LeadAPI.orgId();
    const leadId = resolvedLeadId;
    if (!orgId || !leadId) {
      setNotesError("Organization/Lead not found");
      return;
    }

    setIsCreatingNote(true);
    setNotesError("");
    try {
      const payload = { text, lead: Number(leadId) };
      const res = await LeadAPI.createNote(orgId, leadId, payload);
      const created = res?.data || res;
      const mapped = normalizeNote(created);
      if (mapped?.id) {
        setNotes((prev) => [mapped, ...(Array.isArray(prev) ? prev : [])]);
      }
      setNewNote("");
    } catch (e) {
      setNotesError(e?.message || "Failed to create note");
    } finally {
      setIsCreatingNote(false);
    }
  };

  const handleAddMeeting = async () => {
    const orgId = LeadAPI.orgId();
    const leadId = resolvedLeadId;
    if (!orgId || !leadId) {
      setMeetingsError("Organization/Lead not found");
      return;
    }

    const title = String(newMeeting.title || "").trim();
    const date = String(newMeeting.date || "").trim();
    const time = String(newMeeting.time || "").trim();
    if (!title || !date || !time) return;

    setIsCreatingMeeting(true);
    setMeetingsError("");
    try {
      const payload = {
        lead: Number(leadId),
        title,
        description: newMeeting.description || "",
        date,
        time: toApiTime(time),
        link: newMeeting.link || "",
      };
      const res = await LeadAPI.createMeeting(orgId, leadId, payload);
      const created = res?.data || res;
      const mapped = normalizeMeeting(created);
      if (mapped?.id) {
        setMeetings((prev) => [mapped, ...(Array.isArray(prev) ? prev : [])]);
      }
      setIsAddMeetingOpen(false);
      setNewMeeting({
        title: "",
        date: "",
        time: "",
        description: "",
        link: "",
      });
    } catch (e) {
      setMeetingsError(e?.message || "Failed to schedule meeting");
    } finally {
      setIsCreatingMeeting(false);
    }
  };

  const refreshActivities = async () => {
    const orgId = LeadAPI.orgId();
    const leadId = resolvedLeadId;
    if (!orgId || !leadId) return;
    setActivitiesError("");
    setIsLoadingActivities(true);
    try {
      const res = await LeadAPI.listActivities(orgId, leadId);
      const list = Array.isArray(res) ? res : res?.data || res?.results || [];
      const normalized = (list || [])
        .map(normalizeActivity)
        .filter(Boolean)
        .filter((x) => x.id);
      setActivities(normalized);
    } catch (e) {
      setActivitiesError(e?.message || "Failed to load activities");
    } finally {
      setIsLoadingActivities(false);
    }
  };

  const refreshNotes = async () => {
    const orgId = LeadAPI.orgId();
    const leadId = resolvedLeadId;
    if (!orgId || !leadId) return;
    setNotesError("");
    setIsLoadingNotes(true);
    try {
      const res = await LeadAPI.listNotes(orgId, leadId);
      const list = Array.isArray(res) ? res : res?.data || res?.results || [];
      const normalized = (list || [])
        .map(normalizeNote)
        .filter(Boolean)
        .filter((x) => x.id);
      setNotes(normalized);
    } catch (e) {
      setNotesError(e?.message || "Failed to load notes");
    } finally {
      setIsLoadingNotes(false);
    }
  };

  const refreshMeetings = async () => {
    const orgId = LeadAPI.orgId();
    const leadId = resolvedLeadId;
    if (!orgId || !leadId) return;
    setMeetingsError("");
    setIsLoadingMeetings(true);
    try {
      const res = await LeadAPI.listMeetings(orgId, leadId);
      const list = Array.isArray(res) ? res : res?.data || res?.results || [];
      const normalized = (list || [])
        .map(normalizeMeeting)
        .filter(Boolean)
        .filter((x) => x.id);
      setMeetings(normalized);
    } catch (e) {
      setMeetingsError(e?.message || "Failed to load meetings");
    } finally {
      setIsLoadingMeetings(false);
    }
  };

  useEffect(() => {
    const orgId = LeadAPI.orgId();
    const leadId = resolvedLeadId;
    if (!orgId || !leadId) return;
    if (activeSubTab !== "activity") return;

    refreshActivities();
  }, [activeSubTab, resolvedLeadId]);

  useEffect(() => {
    const orgId = LeadAPI.orgId();
    const leadId = resolvedLeadId;
    if (!orgId || !leadId) return;
    if (activeSubTab !== "meetings") return;

    refreshMeetings();
  }, [activeSubTab, resolvedLeadId]);

  useEffect(() => {
    const orgId = LeadAPI.orgId();
    const leadId = resolvedLeadId;
    if (!orgId || !leadId) return;
    if (activeSubTab !== "notes") return;

    refreshNotes();
  }, [activeSubTab, resolvedLeadId]);

  useEffect(() => {
    setLeadDetails(client);
  }, [client?.id]);

  useEffect(() => {
    const orgId = LeadAPI.orgId();
    const leadId = resolvedLeadId;
    if (!orgId || !leadId) return;

    let cancelled = false;
    (async () => {
      try {
        const res = await LeadAPI.getLead(orgId, leadId);
        const data = res?.data || res;
        if (!cancelled && data) {
          setLeadDetails(data);
        }
      } catch (e) {
        /* ignore */
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [resolvedLeadId, leadRefreshKey]);

  const openEdit = (type, item) => {
    setEditError("");
    setEditModal({ type, item });
    if (type === "activity") setEditValue(item.description || "");
    else if (type === "note") setEditValue(item.text || "");
    else if (type === "meeting") {
      setEditMeeting({
        title: item.title || "",
        date: item.date || "",
        time: item.time || "",
        description: item.description || "",
        link: item.link || "",
      });
    }
  };

  const handleSaveEdit = async () => {
    if (!editModal) return;
    const orgId = LeadAPI.orgId();
    const leadId = resolvedLeadId;
    if (!orgId || !leadId) return;

    setIsSavingEdit(true);
    setEditError("");
    try {
      const { type, item } = editModal;
      if (type === "activity") {
        const description = String(editValue || "").trim();
        if (!description) return;
        setActivities((prev) =>
          prev.map((a) =>
            String(a.id) === String(item.id) ? { ...a, description } : a,
          ),
        );
        await LeadAPI.updateActivity(orgId, leadId, item.id, { description });
        await refreshActivities();
      } else if (type === "note") {
        const text = String(editValue || "").trim();
        if (!text) return;
        setNotes((prev) =>
          prev.map((n) =>
            String(n.id) === String(item.id) ? { ...n, text } : n,
          ),
        );
        await LeadAPI.updateNote(orgId, leadId, item.id, { text });
        await refreshNotes();
      } else if (type === "meeting") {
        const payload = {
          title: String(editMeeting.title || "").trim(),
          date: String(editMeeting.date || "").trim(),
          time: toApiTime(editMeeting.time),
          description: editMeeting.description || "",
          link: editMeeting.link || "",
        };
        if (!payload.title || !payload.date || !payload.time) return;
        setMeetings((prev) =>
          prev.map((m) =>
            String(m.id) === String(item.id)
              ? { ...m, ...payload, time: payload.time.slice(0, 5) }
              : m,
          ),
        );
        await LeadAPI.updateMeeting(orgId, leadId, item.id, payload);
        await refreshMeetings();
      }
      setEditModal(null);
    } catch (e) {
      setEditError(e?.message || "Failed to update");
    } finally {
      setIsSavingEdit(false);
    }
  };

  const handleDelete = async (type, itemId) => {
    const orgId = LeadAPI.orgId();
    const leadId = resolvedLeadId;
    if (!orgId || !leadId) return;

    try {
      if (type === "activity") {
        setActivities((prev) =>
          prev.filter((a) => String(a.id) !== String(itemId)),
        );
        await LeadAPI.deleteActivity(orgId, leadId, itemId);
        await refreshActivities();
      } else if (type === "note") {
        setNotes((prev) => prev.filter((n) => String(n.id) !== String(itemId)));
        await LeadAPI.deleteNote(orgId, leadId, itemId);
        await refreshNotes();
      } else if (type === "meeting") {
        setMeetings((prev) =>
          prev.filter((m) => String(m.id) !== String(itemId)),
        );
        await LeadAPI.deleteMeeting(orgId, leadId, itemId);
        await refreshMeetings();
      }
    } catch (e) {
      // revert by re-fetching
      if (type === "activity") await refreshActivities();
      if (type === "note") await refreshNotes();
      if (type === "meeting") await refreshMeetings();
    }
  };

  const handleCreateActivity = async () => {
    const description = newActivity.trim();
    if (!description) return;

    const orgId = LeadAPI.orgId();
    const leadId = resolvedLeadId;
    if (!orgId || !leadId) {
      setActivitiesError("Organization/Lead not found");
      return;
    }

    setIsCreatingActivity(true);
    setActivitiesError("");
    try {
      const payload = { description, lead: Number(leadId) };
      const res = await LeadAPI.createActivity(orgId, leadId, payload);
      const created = res?.data || res;
      const mapped = normalizeActivity(created);
      if (mapped?.id) {
        setActivities((prev) => [mapped, ...(Array.isArray(prev) ? prev : [])]);
      }
      setNewActivity("");
    } catch (e) {
      setActivitiesError(e?.message || "Failed to create activity");
    } finally {
      setIsCreatingActivity(false);
    }
  };

  const handleCreateScope = async (newScopeData) => {
    const orgId = LeadAPI.orgId();
    const leadId = resolvedLeadId;
    if (!orgId || !leadId) {
      setScopeError("Organization/Lead not found");
      return;
    }
    console.log(newScopeData);
    const name = String(newScopeData.name || "").trim();
    const tentative_start = String(newScopeData.tentative_start || "").trim();
    const property_size = String(newScopeData.property_size || "").trim();
    const status = String(newScopeData.status || "draft").trim() || "draft";
    const budgetNum = Number(
      String(newScopeData.budget || "").replace(/,/g, ""),
    );

    if (
      !name ||
      !tentative_start ||
      !property_size ||
      !Number.isFinite(budgetNum)
    ) {
      setScopeError(
        "Please fill name, tentative start, property size and budget before creating scope.",
      );
      return;
    }

    setIsCreatingScope(true);
    setScopeError("");
    try {
      const payload = {
        name,
        tentative_start,
        budget: budgetNum,
        property_size,
        status,
      };
      await LeadAPI.createScope(orgId, leadId, payload);
      setIsCreateScopeOpen(false);
      setNewScope({
        name: "",
        tentative_start: "",
        property_size: "",
        budget: "",
        status: "draft",
      });
      onCreateScope?.();
    } catch (e) {
      setScopeError(e?.message || "Failed to create scope");
    } finally {
      setIsCreatingScope(false);
    }
  };

  const handleCreateQuotation = async () => {
    const orgId = LeadAPI.orgId();
    const leadId = resolvedLeadId;
    if (!orgId || !leadId) {
      setQuotationError("Organization/Lead not found");
      return;
    }

    const name = String(newQuotation.name || "").trim();
    const payment_terms = String(newQuotation.payment_terms || "").trim();
    const terms_conditions = String(newQuotation.terms_conditions || "").trim();
    if (!name || !payment_terms || !terms_conditions) return;

    setIsCreatingQuotation(true);
    setQuotationError("");
    try {
      // Get scope id for this lead (required by create quotation API)
      const scopeRes = await LeadAPI.getScopeFull(orgId, leadId);
      const scopeData =
        scopeRes?.data && typeof scopeRes?.success !== "undefined"
          ? scopeRes.data
          : scopeRes;
      const scopeId = scopeData?.id;
      if (!scopeId) throw new Error("Scope not found. Create scope first.");

      await LeadAPI.createQuotation(orgId, leadId, {
        scope: Number(scopeId),
        name,
        payment_terms,
        terms_conditions,
      });

      setIsCreateQuotationOpen(false);
      setNewQuotation({ name: "", payment_terms: "", terms_conditions: "" });
      onCreateQuotation?.();
    } catch (e) {
      setQuotationError(e?.message || "Failed to create quotation");
    } finally {
      setIsCreatingQuotation(false);
    }
  };
  return (
    <div className="h-full flex flex-col">
      {/* Sub-tab Navigation */}
      <div className="flex items-center gap-1 mb-3 border-b border-gray-200">
        {subTabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveSubTab(tab.id)}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border-b-2 transition-colors -mb-px ${
              activeSubTab === tab.id
                ? "border-sm-primary text-sm-primary"
                : "border-transparent text-gray-500 hover:text-gray-900"
            }`}
          >
            <tab.icon className="w-3.5 h-3.5" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto">
        {activeSubTab === "overview" && (
          <div className="space-y-4">
            {/* Client Info Card */}
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <div className="p-3 pb-2 flex items-center justify-between">
                <h3 className="text-sm font-semibold flex items-center gap-2 text-gray-900">
                  <User className="w-4 h-4 text-sm-primary" />
                  Client Information
                </h3>
                <div className="flex items-center gap-2">
                  {getStatusBadge(leadInfo?.status)}
                  <button
                    onClick={handleOpenEditInfo}
                    disabled={isEditInfoDisabled}
                    className={`flex items-center gap-1 h-7 px-2 text-xs font-medium border border-gray-300 rounded-md transition-colors ${
                      isEditInfoDisabled
                        ? "cursor-not-allowed opacity-50"
                        : "hover:bg-gray-50"
                    }`}
                  >
                    <Edit className="w-3 h-3" />
                    Edit Info
                  </button>
                </div>
              </div>
              <div className="p-3 pt-0">
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                  <div className="flex items-center gap-2 p-2 rounded-md bg-gray-50 border border-gray-100">
                    <User className="w-4 h-4 text-gray-400" />
                    <div>
                      <p className="text-[10px] text-gray-500">Name</p>
                      <p className="text-xs font-medium text-gray-900">
                        {leadName}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 p-2 rounded-md bg-gray-50 border border-gray-100">
                    <Phone className="w-4 h-4 text-gray-400" />
                    <div>
                      <p className="text-[10px] text-gray-500">Phone</p>
                      <p className="text-xs font-medium text-gray-900">
                        {leadPhone}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 p-2 rounded-md bg-gray-50 border border-gray-100">
                    <Mail className="w-4 h-4 text-gray-400" />
                    <div>
                      <p className="text-[10px] text-gray-500">Email</p>
                      <p className="text-xs font-medium text-gray-900">
                        {leadEmail || "Not provided"}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 p-2 rounded-md bg-gray-50 border border-gray-100">
                    <MapPin className="w-4 h-4 text-gray-400" />
                    <div>
                      <p className="text-[10px] text-gray-500">City</p>
                      <p className="text-xs font-medium text-gray-900">
                        {leadCity}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Project Details Card */}
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <div className="p-3 pb-2 flex items-center justify-between">
                <h3 className="text-sm font-semibold flex items-center gap-2 text-gray-900">
                  <Building2 className="w-4 h-4 text-sm-primary" />
                  Project Details
                </h3>
                <div className="flex items-center gap-2">
                  <button
                    onClick={handleOpenEditProject}
                    disabled={isEditInfoDisabled}
                    className={`flex items-center gap-1 h-7 px-2 text-xs font-medium border border-gray-300 rounded-md transition-colors ${
                      isEditInfoDisabled
                        ? "cursor-not-allowed opacity-50"
                        : "hover:bg-gray-50"
                    }`}
                  >
                    <Edit className="w-3 h-3" />
                    Edit Details
                  </button>
                </div>
              </div>
              <div className="p-3 pt-0">
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                  <div className="flex items-center gap-2 p-2 rounded-md bg-gray-50 border border-gray-100">
                    <Building2 className="w-4 h-4 text-gray-400" />
                    <div>
                      <p className="text-[10px] text-gray-500">Project Type</p>
                      <p className="text-xs font-medium text-gray-900 capitalize">
                        {leadProjectType || "Interior"}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 p-2 rounded-md bg-gray-50 border border-gray-100">
                    <Ruler className="w-4 h-4 text-gray-400" />
                    <div>
                      <p className="text-[10px] text-gray-500">Property Size</p>
                      <p className="text-xs font-medium text-gray-900">
                        {leadPropertySize || "1,200 sq.ft"}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 p-2 rounded-md bg-gray-50 border border-gray-100">
                    <IndianRupee className="w-4 h-4 text-gray-400" />
                    <div>
                      <p className="text-[10px] text-gray-500">Budget Range</p>
                      <p className="text-xs font-medium text-sm-primary">
                        {leadBudgetNumber != null
                          ? formatCurrency(leadBudgetNumber)
                          : "Not specified"}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 p-2 rounded-md bg-gray-50 border border-gray-100">
                    <Calendar className="w-4 h-4 text-gray-400" />
                    <div>
                      <p className="text-[10px] text-gray-500">
                        Tentative Start
                      </p>
                      <p className="text-xs font-medium text-gray-900">
                        {leadStartDate || "TBD"}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 p-2 rounded-md bg-gray-50 border border-gray-100">
                    <Clock className="w-4 h-4 text-gray-400" />
                    <div>
                      <p className="text-[10px] text-gray-500">Lead Since</p>
                      <p className="text-xs font-medium text-gray-900">
                        {leadCreatedAt}
                      </p>
                    </div>
                  </div>
                </div>
                {leadNotes ? (
                  <div className="mt-3 p-2 rounded-md bg-gray-50 border border-gray-100">
                    <p className="text-[10px] text-gray-500 mb-1">
                      Initial Notes
                    </p>
                    <p className="text-xs text-gray-900">{leadNotes}</p>
                  </div>
                ) : null}
              </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <div className="p-3 pb-2">
                <h3 className="text-sm font-semibold text-gray-900">
                  Quick Actions
                </h3>
                <p className="text-[10px] text-gray-500">
                  After discussions and meetings are done, proceed to create
                  scope
                </p>
              </div>
              <div className="p-3 pt-0">
                <div className="flex flex-wrap gap-2">
                  <button
                    className={`flex items-center gap-1.5 h-8 px-3 text-xs font-medium text-white rounded-md transition-colors ${
                      canCreateScope
                        ? "bg-sm-primary hover:bg-sm-hover"
                        : "bg-gray-300 cursor-not-allowed"
                    }`}
                    onClick={() => {
                      if (!canCreateScope) return;
                      console.log("canCreateScope", canCreateScope);
                      setScopeError("");
                      const newScopeData = {
                        name: leadName ? `Scope for ${leadName}` : "",
                        tentative_start: leadStartDate || "",
                        property_size: leadPropertySize || "",
                        budget:
                          leadBudgetNumber != null
                            ? String(leadBudgetNumber)
                            : "",
                        status: "draft",
                      };
                      // setIsCreateScopeOpen(true);
                      handleCreateScope(newScopeData);
                    }}
                    disabled={!canCreateScope}
                  >
                    <FileText className="w-3.5 h-3.5" />
                    Create Scope
                  </button>
                  {/* <button
                    className="flex items-center gap-1.5 h-8 px-3 text-xs font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
                    onClick={() => {
                      setQuotationError("");
                      setNewQuotation({
                      name: leadInfo?.name ? `Quotation for ${leadInfo.name}` : "Quotation",
                        payment_terms: "50% advance, 40% on material delivery, 10% on completion.",
                        terms_conditions: "1. Validity: 15 days.\n2. GST extra as applicable.",
                      });
                      setIsCreateQuotationOpen(true);
                    }}
                  >
                    <FileSpreadsheet className="w-3.5 h-3.5" />
                    Create Quotation
                  </button> */}
                  {/* <button className="flex items-center gap-1.5 h-8 px-3 text-xs font-medium border border-gray-300 rounded-md hover:bg-gray-50 transition-colors">
                    <Phone className="w-3.5 h-3.5" />
                    Call Client
                  </button>
                  <button className="flex items-center gap-1.5 h-8 px-3 text-xs font-medium border border-gray-300 rounded-md hover:bg-gray-50 transition-colors">
                    <MessageCircle className="w-3.5 h-3.5" />
                    WhatsApp
                  </button> */}
                  {canDeleteLead && (
                    <button
                      className="flex items-center gap-1.5 h-8 px-3 text-xs font-medium border border-red-200 text-red-600 rounded-md hover:bg-red-50 transition-colors"
                      onClick={() => {
                        setDeleteError("");
                        setIsDeleteModalOpen(true);
                      }}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                      Delete Lead
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Delete Lead Confirmation Modal */}
        {isDeleteModalOpen && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-sm overflow-hidden">
              <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                <h3 className="text-base font-semibold text-gray-900">
                  Confirm Delete
                </h3>
                <button
                  onClick={() => setIsDeleteModalOpen(false)}
                  className="p-1 rounded-md hover:bg-gray-100 transition-colors"
                  disabled={isDeleting}
                >
                  <X className="w-4 h-4 text-gray-500" />
                </button>
              </div>
              <div className="p-4 space-y-3 text-center">
                {deleteError ? (
                  <div className="px-3 py-2 rounded-md bg-red-50 border border-red-100 text-xs text-red-700">
                    {deleteError}
                  </div>
                ) : null}
                <p className="text-sm text-gray-600 font-medium">
                  Are you sure you want to delete this lead?
                </p>
                <p className="text-xs text-gray-500">
                  This action cannot be undone.
                </p>
              </div>
              <div className="p-4 bg-gray-50 flex items-center justify-between">
                <button
                  className="px-3 py-1.5 text-xs font-medium border border-gray-300 rounded-md hover:bg-gray-100 transition-colors"
                  onClick={() => setIsDeleteModalOpen(false)}
                  disabled={isDeleting}
                >
                  Cancel
                </button>
                <button
                  className={`px-3 py-1.5 text-xs font-medium text-white rounded-md transition-colors ${isDeleting ? "bg-red-300 cursor-not-allowed" : "bg-red-600 hover:bg-red-700"}`}
                  onClick={handleConfirmDeleteLead}
                  disabled={isDeleting}
                >
                  {isDeleting ? "Deleting..." : "Delete Lead"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Create Scope Modal */}
        {isCreateScopeOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md overflow-hidden">
              <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                <h3 className="text-base font-semibold text-gray-900">
                  Create Scope
                </h3>
                <button
                  onClick={() => setIsCreateScopeOpen(false)}
                  className="p-1 rounded-md hover:bg-gray-100 transition-colors"
                  disabled={isCreatingScope}
                >
                  <X className="w-4 h-4 text-gray-500" />
                </button>
              </div>
              <div className="p-4 space-y-3">
                {scopeError ? (
                  <div className="px-3 py-2 rounded-md bg-red-50 border border-red-100 text-xs text-red-700">
                    {scopeError}
                  </div>
                ) : null}

                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-gray-700">
                    Name *
                  </label>
                  <input
                    type="text"
                    placeholder="Interior Design for 3BHK"
                    className="w-full h-8 px-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-sm-primary"
                    value={newScope.name}
                    onChange={(e) =>
                      setNewScope({ ...newScope, name: e.target.value })
                    }
                    disabled={isCreatingScope}
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <label className="text-xs font-medium text-gray-700">
                      Tentative Start *
                    </label>
                    <input
                      type="date"
                      className="w-full h-8 px-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-sm-primary"
                      value={newScope.tentative_start}
                      onChange={(e) =>
                        setNewScope({
                          ...newScope,
                          tentative_start: e.target.value,
                        })
                      }
                      disabled={isCreatingScope}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <label className="text-xs font-medium text-gray-700">
                      Property Size *
                    </label>
                    <input
                      type="text"
                      placeholder="1800 sqft"
                      className="w-full h-8 px-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-sm-primary"
                      value={newScope.property_size}
                      onChange={(e) =>
                        setNewScope({
                          ...newScope,
                          property_size: e.target.value,
                        })
                      }
                      disabled={isCreatingScope}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-xs font-medium text-gray-700">
                      Budget *
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      placeholder="1500000"
                      className="w-full h-8 px-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-sm-primary"
                      value={newScope.budget}
                      onChange={(e) =>
                        setNewScope({ ...newScope, budget: e.target.value })
                      }
                      disabled={isCreatingScope}
                    />
                  </div>
                </div>
              </div>
              <div className="p-4 bg-gray-50 flex justify-end gap-2">
                <button
                  className="px-3 py-1.5 text-xs font-medium border border-gray-300 rounded-md hover:bg-gray-100 transition-colors"
                  onClick={() => setIsCreateScopeOpen(false)}
                  disabled={isCreatingScope}
                >
                  Cancel
                </button>
                <button
                  className={`px-3 py-1.5 text-xs font-medium text-white rounded-md transition-colors ${
                    isCreatingScope ||
                    !String(newScope.name || "").trim() ||
                    !String(newScope.tentative_start || "").trim() ||
                    !String(newScope.property_size || "").trim() ||
                    !Number.isFinite(
                      Number(String(newScope.budget || "").replace(/,/g, "")),
                    )
                      ? "bg-gray-300 cursor-not-allowed"
                      : "bg-sm-primary hover:bg-sm-hover"
                  }`}
                  onClick={handleCreateScope}
                  disabled={
                    isCreatingScope ||
                    !String(newScope.name || "").trim() ||
                    !String(newScope.tentative_start || "").trim() ||
                    !String(newScope.property_size || "").trim() ||
                    !Number.isFinite(
                      Number(String(newScope.budget || "").replace(/,/g, "")),
                    )
                  }
                >
                  {isCreatingScope ? "Creating..." : "Create"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Edit Project Modal */}
        {isEditProjectOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md overflow-hidden">
              <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                <h3 className="text-base font-semibold text-gray-900">
                  Edit Project Details
                </h3>
                <button
                  onClick={() => setIsEditProjectOpen(false)}
                  className="p-1 rounded-md hover:bg-gray-100 transition-colors"
                  disabled={isSavingProject}
                >
                  <X className="w-4 h-4 text-gray-500" />
                </button>
              </div>
              <div className="p-4 space-y-3">
                {editProjectError ? (
                  <div className="px-3 py-2 rounded-md bg-red-50 border border-red-100 text-xs text-red-700">
                    {editProjectError}
                  </div>
                ) : null}

                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-gray-700">
                    Project Type
                  </label>
                  <select
                    className="w-full h-8 px-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-sm-primary"
                    value={editProjectForm.project_type}
                    onChange={(e) =>
                      setEditProjectForm({
                        ...editProjectForm,
                        project_type: e.target.value,
                      })
                    }
                    disabled={isSavingProject}
                  >
                    <option value="interior">Interior</option>
                    <option value="construction">Construction</option>
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <label className="text-xs font-medium text-gray-700">
                      Property Size
                    </label>
                    <input
                      type="text"
                      className="w-full h-8 px-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-sm-primary"
                      value={editProjectForm.property_size}
                      onChange={(e) =>
                        setEditProjectForm({
                          ...editProjectForm,
                          property_size: e.target.value,
                        })
                      }
                      disabled={isSavingProject}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-xs font-medium text-gray-700">
                      Budget
                    </label>
                    <input
                      type="number"
                      className="w-full h-8 px-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-sm-primary"
                      value={editProjectForm.budget}
                      onChange={(e) =>
                        setEditProjectForm({
                          ...editProjectForm,
                          budget: e.target.value,
                        })
                      }
                      disabled={isSavingProject}
                    />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-gray-700">
                    Tentative Start Date
                  </label>
                  <input
                    type="date"
                    className="w-full h-8 px-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-sm-primary"
                    value={editProjectForm.start_date}
                    onChange={(e) =>
                      setEditProjectForm({
                        ...editProjectForm,
                        start_date: e.target.value,
                      })
                    }
                    disabled={isSavingProject}
                  />
                </div>
              </div>
              <div className="p-4 bg-gray-50 flex justify-end gap-2">
                <button
                  className="px-3 py-1.5 text-xs font-medium border border-gray-300 rounded-md hover:bg-gray-100 transition-colors"
                  onClick={() => setIsEditProjectOpen(false)}
                  disabled={isSavingProject}
                >
                  Cancel
                </button>
                <button
                  className={`px-3 py-1.5 text-xs font-medium text-white rounded-md transition-colors ${isSavingProject ? "bg-gray-300 cursor-not-allowed" : "bg-sm-primary hover:bg-sm-hover"}`}
                  onClick={handleSaveEditProject}
                  disabled={isSavingProject}
                >
                  {isSavingProject ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Create Quotation Modal */}
        {isCreateQuotationOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-lg overflow-hidden">
              <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                <h3 className="text-base font-semibold text-gray-900">
                  Create Quotation
                </h3>
                <button
                  onClick={() => setIsCreateQuotationOpen(false)}
                  className="p-1 rounded-md hover:bg-gray-100 transition-colors"
                  disabled={isCreatingQuotation}
                >
                  <X className="w-4 h-4 text-gray-500" />
                </button>
              </div>
              <div className="p-4 space-y-3">
                {quotationError ? (
                  <div className="px-3 py-2 rounded-md bg-red-50 border border-red-100 text-xs text-red-700">
                    {quotationError}
                  </div>
                ) : null}

                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-gray-700">
                    Name *
                  </label>
                  <input
                    type="text"
                    placeholder="Quotation for 3BHK Interior"
                    className="w-full h-8 px-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-sm-primary"
                    value={newQuotation.name}
                    onChange={(e) =>
                      setNewQuotation({ ...newQuotation, name: e.target.value })
                    }
                    disabled={isCreatingQuotation}
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-gray-700">
                    Payment Terms *
                  </label>
                  <textarea
                    className="w-full min-h-[70px] p-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-sm-primary resize-none"
                    value={newQuotation.payment_terms}
                    onChange={(e) =>
                      setNewQuotation({
                        ...newQuotation,
                        payment_terms: e.target.value,
                      })
                    }
                    disabled={isCreatingQuotation}
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-gray-700">
                    Terms & Conditions *
                  </label>
                  <textarea
                    className="w-full min-h-[90px] p-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-sm-primary resize-none"
                    value={newQuotation.terms_conditions}
                    onChange={(e) =>
                      setNewQuotation({
                        ...newQuotation,
                        terms_conditions: e.target.value,
                      })
                    }
                    disabled={isCreatingQuotation}
                  />
                </div>
              </div>
              <div className="p-4 bg-gray-50 flex justify-end gap-2">
                <button
                  className="px-3 py-1.5 text-xs font-medium border border-gray-300 rounded-md hover:bg-gray-100 transition-colors"
                  onClick={() => setIsCreateQuotationOpen(false)}
                  disabled={isCreatingQuotation}
                >
                  Cancel
                </button>
                <button
                  className={`px-3 py-1.5 text-xs font-medium text-white rounded-md transition-colors ${
                    isCreatingQuotation ||
                    !newQuotation.name.trim() ||
                    !newQuotation.payment_terms.trim() ||
                    !newQuotation.terms_conditions.trim()
                      ? "bg-gray-300 cursor-not-allowed"
                      : "bg-sm-primary hover:bg-sm-hover"
                  }`}
                  onClick={handleCreateQuotation}
                  disabled={
                    isCreatingQuotation ||
                    !newQuotation.name.trim() ||
                    !newQuotation.payment_terms.trim() ||
                    !newQuotation.terms_conditions.trim()
                  }
                >
                  {isCreatingQuotation ? "Creating..." : "Create"}
                </button>
              </div>
            </div>
          </div>
        )}

        {activeSubTab === "activity" && (
          <div className="space-y-3">
            {/* Add Activity */}
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <div className="p-3">
                <div className="flex gap-2">
                  <textarea
                    placeholder="Add an activity update (call, meeting, note shared, etc.)"
                    className="flex-1 min-h-[60px] p-2 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-sm-primary resize-none"
                    value={newActivity}
                    onChange={(e) => setNewActivity(e.target.value)}
                    disabled={isCreatingActivity}
                  />
                  <button
                    className={`flex items-center justify-center h-10 w-10 text-white rounded-md transition-colors ${
                      isCreatingActivity || !newActivity.trim()
                        ? "bg-gray-300 cursor-not-allowed"
                        : "bg-sm-primary hover:bg-sm-hover"
                    }`}
                    onClick={handleCreateActivity}
                    disabled={isCreatingActivity || !newActivity.trim()}
                    title="Add activity"
                  >
                    <Send className="w-4 h-4" />
                  </button>
                </div>
                {activitiesError ? (
                  <div className="mt-2 text-[10px] text-red-700 bg-red-50 border border-red-100 rounded-md px-2 py-1.5">
                    {activitiesError}
                  </div>
                ) : null}
              </div>
            </div>

            {/* Activity Timeline */}
            <div className="bg-white rounded-lg border border-gray-200">
              <div className="p-3 pb-2">
                <h3 className="text-sm font-semibold flex items-center gap-2 text-gray-900">
                  <Activity className="w-4 h-4 text-sm-primary" />
                  Activity Timeline ({activities.length})
                </h3>
              </div>
              <div className="p-3 pt-0">
                {isLoadingActivities ? (
                  <div className="text-xs text-gray-500 py-2">
                    Loading activities...
                  </div>
                ) : null}

                {!isLoadingActivities && activities.length === 0 ? (
                  <div className="text-xs text-gray-500 py-2">
                    No activity yet.
                  </div>
                ) : null}

                <div className="relative">
                  <div className="absolute left-2 top-0 bottom-0 w-px bg-gray-200" />
                  <div className="space-y-3">
                    {activities.map((activity, index) => (
                      <div key={activity.id} className="flex gap-3 relative">
                        <div
                          className={`w-4 h-4 rounded-full border-2 bg-white z-10 flex-shrink-0 ${
                            index === 0
                              ? "border-sm-primary"
                              : "border-gray-300"
                          }`}
                        />
                        <div className="flex-1 pb-3">
                          <div className="flex items-start justify-between gap-2 mb-0.5">
                            <p className="text-xs font-medium text-gray-900">
                              {activity.description}
                            </p>
                            <div className="relative menu-container">
                              <button
                                className="h-6 w-6 flex items-center justify-center rounded-md hover:bg-gray-100 transition-colors"
                                onClick={() =>
                                  setOpenMenu((prev) =>
                                    prev?.type === "activity" &&
                                    prev?.id === String(activity.id)
                                      ? null
                                      : {
                                          type: "activity",
                                          id: String(activity.id),
                                        },
                                  )
                                }
                              >
                                <MoreVertical className="w-3.5 h-3.5 text-gray-500" />
                              </button>
                              {openMenu?.type === "activity" &&
                              openMenu?.id === String(activity.id) ? (
                                <div className="absolute right-0 bottom-full mb-1 w-28 bg-white border border-gray-200 rounded-md shadow-lg overflow-hidden z-10">
                                  <button
                                    className="w-full px-3 py-2 text-xs text-left hover:bg-gray-50 flex items-center gap-2"
                                    onClick={() => {
                                      closeMenu();
                                      openEdit("activity", activity);
                                    }}
                                  >
                                    <Pencil className="w-3.5 h-3.5 text-gray-500" />
                                    Edit
                                  </button>
                                  <button
                                    className="w-full px-3 py-2 text-xs text-left hover:bg-gray-50 flex items-center gap-2 text-red-600"
                                    onClick={() => {
                                      closeMenu();
                                      handleDelete("activity", activity.id);
                                    }}
                                  >
                                    <Trash2 className="w-3.5 h-3.5" />
                                    Delete
                                  </button>
                                </div>
                              ) : null}
                            </div>
                          </div>
                          <div className="flex items-center gap-2 text-[10px] text-gray-500">
                            <span>{activity.date}</span>
                            <span>•</span>
                            <span className="text-gray-500">{activity.by}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeSubTab === "notes" && (
          <div className="space-y-3">
            {/* Add Note */}
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <div className="p-3">
                <div className="flex gap-2">
                  <textarea
                    placeholder="Add a private note about this lead..."
                    className="flex-1 min-h-[60px] p-2 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-sm-primary resize-none"
                    value={newNote}
                    onChange={(e) => setNewNote(e.target.value)}
                    disabled={isCreatingNote}
                  />
                  <button
                    className={`flex items-center justify-center h-10 w-10 text-white rounded-md transition-colors ${
                      isCreatingNote || !newNote.trim()
                        ? "bg-gray-300 cursor-not-allowed"
                        : "bg-sm-primary hover:bg-sm-hover"
                    }`}
                    onClick={handleAddNote}
                    disabled={isCreatingNote || !newNote.trim()}
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
                {notesError ? (
                  <div className="mt-2 text-[10px] text-red-700 bg-red-50 border border-red-100 rounded-md px-2 py-1.5">
                    {notesError}
                  </div>
                ) : null}
                <p className="text-[10px] text-gray-500 mt-1.5">
                  Notes are visible only to internal team members
                </p>
              </div>
            </div>

            {/* Notes List */}
            <div className="bg-white rounded-lg border border-gray-200">
              <div className="p-3 pb-2">
                <h3 className="text-sm font-semibold flex items-center gap-2 text-gray-900">
                  <StickyNote className="w-4 h-4 text-sm-primary" />
                  Internal Notes ({notes.length})
                </h3>
              </div>
              <div className="p-3 pt-0">
                {isLoadingNotes ? (
                  <div className="text-xs text-gray-500 py-2">
                    Loading notes...
                  </div>
                ) : null}
                {!isLoadingNotes && notes.length === 0 ? (
                  <div className="text-xs text-gray-500 py-2">
                    No notes yet.
                  </div>
                ) : null}
                <div className="space-y-2">
                  {notes.map((note) => (
                    <div
                      key={note.id}
                      className="p-2 rounded-md bg-gray-50 border border-gray-100"
                    >
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-medium text-gray-900">
                            {note.author}
                          </span>
                          <span className="text-[10px] text-gray-500">
                            {note.timestamp}
                          </span>
                        </div>
                        <div className="relative menu-container">
                          <button
                            className="h-6 w-6 flex items-center justify-center rounded-md hover:bg-gray-100 transition-colors"
                            onClick={() =>
                              setOpenMenu((prev) =>
                                prev?.type === "note" &&
                                prev?.id === String(note.id)
                                  ? null
                                  : { type: "note", id: String(note.id) },
                              )
                            }
                          >
                            <MoreVertical className="w-3.5 h-3.5 text-gray-500" />
                          </button>
                          {openMenu?.type === "note" &&
                          openMenu?.id === String(note.id) ? (
                            <div className="absolute right-0 bottom-full mb-1 w-28 bg-white border border-gray-200 rounded-md shadow-lg overflow-hidden z-10">
                              <button
                                className="w-full px-3 py-2 text-xs text-left hover:bg-gray-50 flex items-center gap-2"
                                onClick={() => {
                                  closeMenu();
                                  openEdit("note", note);
                                }}
                              >
                                <Pencil className="w-3.5 h-3.5 text-gray-500" />
                                Edit
                              </button>
                              <button
                                className="w-full px-3 py-2 text-xs text-left hover:bg-gray-50 flex items-center gap-2 text-red-600"
                                onClick={() => {
                                  closeMenu();
                                  handleDelete("note", note.id);
                                }}
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                                Delete
                              </button>
                            </div>
                          ) : null}
                        </div>
                      </div>
                      <p className="text-xs text-gray-600">{note.text}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {activeSubTab === "meetings" && (
          <div className="space-y-3">
            {/* Add Meeting Button */}
            <div className="flex justify-end">
              <button
                className="flex items-center gap-1.5 h-8 px-3 text-xs font-medium text-white bg-sm-primary rounded-md hover:bg-sm-hover transition-colors"
                onClick={() => setIsAddMeetingOpen(true)}
              >
                <Plus className="w-3.5 h-3.5" />
                Schedule Meeting
              </button>
            </div>

            {/* Schedule Meeting Modal */}
            {isAddMeetingOpen && (
              <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
                <div className="bg-white rounded-lg shadow-xl w-full max-w-md overflow-hidden">
                  <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                    <h3 className="text-base font-semibold text-gray-900">
                      Schedule Meeting
                    </h3>
                    <button
                      onClick={() => setIsAddMeetingOpen(false)}
                      className="p-1 rounded-md hover:bg-gray-100 transition-colors"
                    >
                      <X className="w-4 h-4 text-gray-500" />
                    </button>
                  </div>
                  <div className="p-4 space-y-3">
                    <div className="space-y-1.5">
                      <label className="text-xs font-medium text-gray-700">
                        Meeting Title *
                      </label>
                      <input
                        type="text"
                        placeholder="e.g., Design Review Meeting"
                        className="w-full h-8 px-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-sm-primary"
                        value={newMeeting.title}
                        onChange={(e) =>
                          setNewMeeting({
                            ...newMeeting,
                            title: e.target.value,
                          })
                        }
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1.5">
                        <label className="text-xs font-medium text-gray-700">
                          Date *
                        </label>
                        <input
                          type="date"
                          className="w-full h-8 px-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-sm-primary"
                          value={newMeeting.date}
                          onChange={(e) =>
                            setNewMeeting({
                              ...newMeeting,
                              date: e.target.value,
                            })
                          }
                        />
                      </div>
                      <div className="space-y-1.5">
                        <label className="text-xs font-medium text-gray-700">
                          Time *
                        </label>
                        <input
                          type="time"
                          className="w-full h-8 px-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-sm-primary"
                          value={newMeeting.time}
                          onChange={(e) =>
                            setNewMeeting({
                              ...newMeeting,
                              time: e.target.value,
                            })
                          }
                        />
                      </div>
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-xs font-medium text-gray-700">
                        Description
                      </label>
                      <textarea
                        placeholder="Meeting agenda..."
                        className="w-full min-h-[60px] p-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-sm-primary resize-none"
                        value={newMeeting.description}
                        onChange={(e) =>
                          setNewMeeting({
                            ...newMeeting,
                            description: e.target.value,
                          })
                        }
                      />
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-xs font-medium text-gray-700">
                        Meeting Link (optional)
                      </label>
                      <input
                        type="text"
                        placeholder="https://meet.google.com/..."
                        className="w-full h-8 px-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-sm-primary"
                        value={newMeeting.link}
                        onChange={(e) =>
                          setNewMeeting({ ...newMeeting, link: e.target.value })
                        }
                      />
                      <p className="text-[10px] text-gray-500">
                        Leave empty to auto-generate
                      </p>
                    </div>
                  </div>
                  <div className="p-4 bg-gray-50 flex justify-end gap-2">
                    <button
                      className="px-3 py-1.5 text-xs font-medium border border-gray-300 rounded-md hover:bg-gray-100 transition-colors"
                      onClick={() => setIsAddMeetingOpen(false)}
                      disabled={isCreatingMeeting}
                    >
                      Cancel
                    </button>
                    <button
                      className={`px-3 py-1.5 text-xs font-medium text-white rounded-md transition-colors ${
                        isCreatingMeeting ||
                        !newMeeting.title ||
                        !newMeeting.date ||
                        !newMeeting.time
                          ? "bg-gray-300 cursor-not-allowed"
                          : "bg-sm-primary hover:bg-sm-hover"
                      }`}
                      onClick={handleAddMeeting}
                      disabled={
                        isCreatingMeeting ||
                        !newMeeting.title ||
                        !newMeeting.date ||
                        !newMeeting.time
                      }
                    >
                      {isCreatingMeeting ? "Scheduling..." : "Schedule"}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {meetingsError ? (
              <div className="px-3 py-2 rounded-md bg-red-50 border border-red-100 text-xs text-red-700">
                {meetingsError}
              </div>
            ) : null}

            {/* Upcoming Meetings */}
            <div className="bg-white rounded-lg border border-gray-200">
              <div className="p-3 pb-2">
                <h3 className="text-sm font-semibold flex items-center gap-2 text-gray-900">
                  <Calendar className="w-4 h-4 text-sm-primary" />
                  Upcoming Meetings
                </h3>
              </div>
              <div className="p-3 pt-0">
                {isLoadingMeetings ? (
                  <div className="text-xs text-gray-500 py-2">
                    Loading meetings...
                  </div>
                ) : null}
                <div className="space-y-2">
                  {meetings
                    .filter((m) => {
                      const ms = meetingStartMs(m);
                      return Number.isFinite(ms) ? ms >= Date.now() : true;
                    })
                    .sort((a, b) => meetingStartMs(a) - meetingStartMs(b))
                    .map((meeting) => (
                      <div
                        key={meeting.id}
                        className="p-2 rounded-md bg-sm-primary/5 border border-sm-primary/10"
                      >
                        <div className="flex items-start justify-between mb-1">
                          <div>
                            <p className="text-xs font-medium text-gray-900">
                              {meeting.title}
                            </p>
                            <p className="text-[10px] text-gray-500">
                              {meeting.description}
                            </p>
                          </div>
                          <div className="text-right flex items-start gap-2">
                            <div>
                              <p className="text-xs font-medium text-sm-primary">
                                {meeting.date}
                              </p>
                              <p className="text-[10px] text-gray-500">
                                {meeting.time}
                              </p>
                            </div>
                            <div className="relative menu-container">
                              <button
                                className="h-6 w-6 flex items-center justify-center rounded-md hover:bg-gray-100 transition-colors"
                                onClick={() =>
                                  setOpenMenu((prev) =>
                                    prev?.type === "meeting" &&
                                    prev?.id === String(meeting.id)
                                      ? null
                                      : {
                                          type: "meeting",
                                          id: String(meeting.id),
                                        },
                                  )
                                }
                              >
                                <MoreVertical className="w-3.5 h-3.5 text-gray-500" />
                              </button>
                              {openMenu?.type === "meeting" &&
                              openMenu?.id === String(meeting.id) ? (
                                <div className="absolute right-0 bottom-full mb-1 w-28 bg-white border border-gray-200 rounded-md shadow-lg overflow-hidden z-10">
                                  <button
                                    className="w-full px-3 py-2 text-xs text-left hover:bg-gray-50 flex items-center gap-2"
                                    onClick={() => {
                                      closeMenu();
                                      openEdit("meeting", meeting);
                                    }}
                                  >
                                    <Pencil className="w-3.5 h-3.5 text-gray-500" />
                                    Edit
                                  </button>
                                  <button
                                    className="w-full px-3 py-2 text-xs text-left hover:bg-gray-50 flex items-center gap-2 text-red-600"
                                    onClick={() => {
                                      closeMenu();
                                      handleDelete("meeting", meeting.id);
                                    }}
                                  >
                                    <Trash2 className="w-3.5 h-3.5" />
                                    Delete
                                  </button>
                                </div>
                              ) : null}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-1.5 mt-2">
                          <button className="flex items-center gap-1 h-6 px-2 text-[10px] font-medium border border-gray-300 rounded-md hover:bg-gray-50 transition-colors">
                            <Send className="w-2.5 h-2.5" />
                            Email
                          </button>
                          {/* <button className="flex items-center gap-1 h-6 px-2 text-[10px] font-medium border border-gray-300 rounded-md hover:bg-gray-50 transition-colors">
                            <MessageCircle className="w-2.5 h-2.5" />
                            WhatsApp
                          </button> */}
                          {meeting.link && (
                            <button
                              className="flex items-center gap-1 h-6 px-2 text-[10px] font-medium text-white bg-sm-primary rounded-md hover:bg-sm-hover transition-colors ml-auto"
                              onClick={() =>
                                window.open(
                                  meeting.link,
                                  "_blank",
                                  "noopener,noreferrer",
                                )
                              }
                            >
                              <ExternalLink className="w-2.5 h-2.5" />
                              Join
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            </div>

            {/* Past Meetings */}
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <div className="p-3 pb-2">
                <h3 className="text-sm font-semibold flex items-center gap-2 text-gray-500">
                  <Clock className="w-4 h-4" />
                  Past Meetings
                </h3>
              </div>
              <div className="p-3 pt-0">
                <div className="space-y-2">
                  {meetings
                    .filter((m) => {
                      const ms = meetingStartMs(m);
                      return Number.isFinite(ms) ? ms < Date.now() : false;
                    })
                    .sort((a, b) => meetingStartMs(b) - meetingStartMs(a))
                    .map((meeting) => (
                      <div
                        key={meeting.id}
                        className="p-2 rounded-md bg-gray-50 border border-gray-100 opacity-75"
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="text-xs font-medium text-gray-900">
                              {meeting.title}
                            </p>
                            <p className="text-[10px] text-gray-500">
                              {meeting.description}
                            </p>
                          </div>
                          <div className="text-right flex items-start gap-2">
                            <div>
                              <p className="text-xs text-gray-500">
                                {meeting.date}
                              </p>
                              <p className="text-[10px] text-gray-500">
                                {meeting.time}
                              </p>
                            </div>
                            <div className="relative">
                              <button
                                className="h-6 w-6 flex items-center justify-center rounded-md hover:bg-gray-100 transition-colors"
                                onClick={() =>
                                  setOpenMenu((prev) =>
                                    prev?.type === "meeting" &&
                                    prev?.id === String(meeting.id)
                                      ? null
                                      : {
                                          type: "meeting",
                                          id: String(meeting.id),
                                        },
                                  )
                                }
                              >
                                <MoreVertical className="w-3.5 h-3.5 text-gray-500" />
                              </button>
                              {openMenu?.type === "meeting" &&
                              openMenu?.id === String(meeting.id) ? (
                                <div className="absolute right-0 mt-1 w-28 bg-white border border-gray-200 rounded-md shadow-lg overflow-hidden z-10">
                                  <button
                                    className="w-full px-3 py-2 text-xs text-left hover:bg-gray-50 flex items-center gap-2"
                                    onClick={() => {
                                      closeMenu();
                                      openEdit("meeting", meeting);
                                    }}
                                  >
                                    <Pencil className="w-3.5 h-3.5 text-gray-500" />
                                    Edit
                                  </button>
                                  <button
                                    className="w-full px-3 py-2 text-xs text-left hover:bg-gray-50 flex items-center gap-2 text-red-600"
                                    onClick={() => {
                                      closeMenu();
                                      handleDelete("meeting", meeting.id);
                                    }}
                                  >
                                    <Trash2 className="w-3.5 h-3.5" />
                                    Delete
                                  </button>
                                </div>
                              ) : null}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Edit Client Info Modal */}
        {isEditInfoOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md overflow-hidden">
              <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                <h3 className="text-base font-semibold text-gray-900">
                  Edit Client Details
                </h3>
                <button
                  onClick={() => setIsEditInfoOpen(false)}
                  className="p-1 rounded-md hover:bg-gray-100 transition-colors"
                  disabled={isSavingInfo}
                >
                  <X className="w-4 h-4 text-gray-500" />
                </button>
              </div>
              <div className="p-4 space-y-3">
                {editInfoError ? (
                  <div className="px-3 py-2 rounded-md bg-red-50 border border-red-100 text-xs text-red-700">
                    {editInfoError}
                  </div>
                ) : null}

                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-gray-700">
                    Name *
                  </label>
                  <input
                    type="text"
                    className="w-full h-8 px-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-sm-primary"
                    value={editInfoForm.name}
                    onChange={(e) =>
                      setEditInfoForm({ ...editInfoForm, name: e.target.value })
                    }
                    disabled={isSavingInfo}
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-gray-700">
                    Phone *
                  </label>
                  <div
                    className={`flex items-center bg-gray-50 px-2 rounded-md border ${
                      phoneError ? "border-red-500" : "border-gray-300"
                    }`}
                  >
                    <span className="text-sm text-gray-500 mr-1 shrink-0">
                      +91
                    </span>
                    <input
                      type="text"
                      maxLength={10}
                      className="w-full h-8 text-sm bg-transparent focus:outline-none"
                      value={editInfoForm.phone}
                      onInput={handlePhoneInput}
                      placeholder="9999999999"
                      disabled={isSavingInfo}
                    />
                  </div>
                  {phoneError && (
                    <p className="text-red-500 text-[10px] mt-1">
                      {phoneError}
                    </p>
                  )}
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-gray-700">
                    Email
                  </label>
                  <input
                    type="email"
                    className="w-full h-8 px-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-sm-primary"
                    value={editInfoForm.email}
                    onChange={(e) =>
                      setEditInfoForm({
                        ...editInfoForm,
                        email: e.target.value,
                      })
                    }
                    disabled={isSavingInfo}
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-gray-700">
                    City
                  </label>
                  <input
                    type="text"
                    className="w-full h-8 px-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-sm-primary"
                    value={editInfoForm.city}
                    onChange={(e) =>
                      setEditInfoForm({ ...editInfoForm, city: e.target.value })
                    }
                    disabled={isSavingInfo}
                  />
                </div>
              </div>
              <div className="p-4 bg-gray-50 flex justify-end gap-2">
                <button
                  className="px-3 py-1.5 text-xs font-medium border border-gray-300 rounded-md hover:bg-gray-100 transition-colors"
                  onClick={() => setIsEditInfoOpen(false)}
                  disabled={isSavingInfo}
                >
                  Cancel
                </button>
                <button
                  className={`px-3 py-1.5 text-xs font-medium text-white rounded-md transition-colors ${
                    isEditInfoSaveDisabled
                      ? "bg-gray-300 cursor-not-allowed"
                      : "bg-sm-primary hover:bg-sm-hover"
                  }`}
                  onClick={handleSaveEditInfo}
                  disabled={isEditInfoSaveDisabled}
                >
                  {isSavingInfo ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Edit Modal (Activity/Note/Meeting) */}
        {editModal && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
            onMouseDown={() => {
              closeMenu();
            }}
          >
            <div
              className="bg-white rounded-lg shadow-xl w-full max-w-md overflow-hidden"
              onMouseDown={(e) => e.stopPropagation()}
            >
              <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                <h3 className="text-base font-semibold text-gray-900">
                  Edit{" "}
                  {editModal.type === "activity"
                    ? "Activity"
                    : editModal.type === "note"
                      ? "Note"
                      : "Meeting"}
                </h3>
                <button
                  onClick={() => setEditModal(null)}
                  className="p-1 rounded-md hover:bg-gray-100 transition-colors"
                  disabled={isSavingEdit}
                >
                  <X className="w-4 h-4 text-gray-500" />
                </button>
              </div>

              <div className="p-4 space-y-3">
                {editError ? (
                  <div className="px-3 py-2 rounded-md bg-red-50 border border-red-100 text-xs text-red-700">
                    {editError}
                  </div>
                ) : null}

                {(editModal.type === "activity" ||
                  editModal.type === "note") && (
                  <textarea
                    className="w-full min-h-[90px] p-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-sm-primary resize-none"
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    disabled={isSavingEdit}
                  />
                )}

                {editModal.type === "meeting" && (
                  <div className="space-y-3">
                    <div className="space-y-1.5">
                      <label className="text-xs font-medium text-gray-700">
                        Title *
                      </label>
                      <input
                        className="w-full h-8 px-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-sm-primary"
                        value={editMeeting.title}
                        onChange={(e) =>
                          setEditMeeting({
                            ...editMeeting,
                            title: e.target.value,
                          })
                        }
                        disabled={isSavingEdit}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1.5">
                        <label className="text-xs font-medium text-gray-700">
                          Date *
                        </label>
                        <input
                          type="date"
                          className="w-full h-8 px-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-sm-primary"
                          value={editMeeting.date}
                          onChange={(e) =>
                            setEditMeeting({
                              ...editMeeting,
                              date: e.target.value,
                            })
                          }
                          disabled={isSavingEdit}
                        />
                      </div>
                      <div className="space-y-1.5">
                        <label className="text-xs font-medium text-gray-700">
                          Time *
                        </label>
                        <input
                          type="time"
                          className="w-full h-8 px-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-sm-primary"
                          value={editMeeting.time}
                          onChange={(e) =>
                            setEditMeeting({
                              ...editMeeting,
                              time: e.target.value,
                            })
                          }
                          disabled={isSavingEdit}
                        />
                      </div>
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-xs font-medium text-gray-700">
                        Description
                      </label>
                      <textarea
                        className="w-full min-h-[70px] p-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-sm-primary resize-none"
                        value={editMeeting.description}
                        onChange={(e) =>
                          setEditMeeting({
                            ...editMeeting,
                            description: e.target.value,
                          })
                        }
                        disabled={isSavingEdit}
                      />
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-xs font-medium text-gray-700">
                        Link
                      </label>
                      <input
                        className="w-full h-8 px-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-sm-primary"
                        value={editMeeting.link}
                        onChange={(e) =>
                          setEditMeeting({
                            ...editMeeting,
                            link: e.target.value,
                          })
                        }
                        disabled={isSavingEdit}
                      />
                    </div>
                  </div>
                )}
              </div>

              <div className="p-4 bg-gray-50 flex justify-end gap-2">
                <button
                  className="px-3 py-1.5 text-xs font-medium border border-gray-300 rounded-md hover:bg-gray-100 transition-colors"
                  onClick={() => setEditModal(null)}
                  disabled={isSavingEdit}
                >
                  Cancel
                </button>
                <button
                  className={`px-3 py-1.5 text-xs font-medium text-white rounded-md transition-colors ${
                    isSavingEdit
                      ? "bg-gray-300 cursor-not-allowed"
                      : "bg-sm-primary hover:bg-sm-hover"
                  }`}
                  onClick={handleSaveEdit}
                  disabled={isSavingEdit}
                >
                  {isSavingEdit ? "Saving..." : "Save"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
